import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImagePlus, MousePointerClick, Palette, Settings2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { ColorPicker } from '@/shared/ui/color-picker';
import { Switch } from '@/shared/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import type { FabButton, FabButtonType } from '@/shared/types/fab-button';
import { FAB_TYPE_META, selectableFabTypes } from '../model/fab-button-meta';
import { buildMgmFabUrl, mgmFabIconSvg, MGM_FAB_DEFAULT_LABEL } from '../model/mgm-fab';
import { FabIcon } from './fab-icon';
import { MgmFabLink, MgmFabPreview } from './mgm-fab-preview';
import { applySvgColor, defaultFabIconSvg, readSvgFile, resolveFabIconSrc } from '../model/fab-svg';
import { saveFabButton } from '../api/use-fab-buttons';

interface Props {
  idPassbook: string;
  fab: FabButton | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  /** Passbook com o módulo Indique e Ganhe e sem outro botão desse tipo. */
  allowMgm?: boolean;
}

const DEFAULT_COLOR = '#2563eb';
const DEFAULT_ICON_COLOR = '#ffffff';

async function fetchExistingSvg(iconUrl: string | undefined): Promise<string | null> {
  if (!iconUrl) return null;
  try {
    const res = await fetch(resolveFabIconSrc(iconUrl));
    if (!res.ok) return null;
    const text = await res.text();
    return text.includes('<svg') ? text : null;
  } catch {
    return null;
  }
}

export function FabButtonFormDialog({ idPassbook, fab, open, onOpenChange, onSaved, allowMgm = false }: Props) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState<FabButtonType>('whatsapp');
  const [value, setValue] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [iconColor, setIconColor] = useState(DEFAULT_ICON_COLOR);
  const [initialIconColor, setInitialIconColor] = useState(DEFAULT_ICON_COLOR);
  const [active, setActive] = useState(true);
  const [launchDirect, setLaunchDirect] = useState(true);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [baseSvg, setBaseSvg] = useState<string | null>(null);
  const [iconSvgPreview, setIconSvgPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isInsert = !fab;
  const meta = FAB_TYPE_META[type];
  const PreviewIcon = meta.icon;
  const isMgm = type === 'mgm';
  const mgmUrl = buildMgmFabUrl(idPassbook);
  // Editando o próprio botão MGM o tipo continua disponível.
  const typeOptions = selectableFabTypes(allowMgm || fab?.type === 'mgm');

  const changeType = (next: FabButtonType) => {
    setType(next);
    if (next === 'mgm') {
      setValue(buildMgmFabUrl(idPassbook));
      setLaunchDirect(false);
      if (!label.trim()) setLabel(MGM_FAB_DEFAULT_LABEL);
    } else if (type === 'mgm') {
      setValue('');
      setLaunchDirect(true);
    }
  };

  useEffect(() => {
    if (!open) return;
    const nextIconColor = fab?.iconColor ?? fab?.foregroundColor ?? DEFAULT_ICON_COLOR;
    setLabel(fab?.label ?? '');
    setType(fab?.type ?? 'whatsapp');
    setValue(fab?.value ?? '');
    setColor(fab?.color ?? DEFAULT_COLOR);
    setIconColor(nextIconColor);
    setInitialIconColor(nextIconColor);
    setActive(fab?.active ?? true);
    setLaunchDirect(fab?.launchDirect ?? true);
    setIconFile(null);
    setBaseSvg(null);
    setIconSvgPreview(null);

    if (fab?.iconUrl) {
      void fetchExistingSvg(fab.iconUrl).then((svg) => {
        if (svg) setBaseSvg(svg);
      });
    }
  }, [open, fab]);

  useEffect(() => {
    let cancelled = false;

    async function updatePreview() {
      let raw: string;
      if (iconFile) {
        raw = await readSvgFile(iconFile);
      } else if (baseSvg) {
        raw = baseSvg;
      } else if (isInsert) {
        raw = type === 'mgm' ? mgmFabIconSvg() : defaultFabIconSvg();
      } else {
        if (!cancelled) setIconSvgPreview(null);
        return;
      }
      if (!cancelled) setIconSvgPreview(applySvgColor(raw, iconColor));
    }

    void updatePreview();
    return () => {
      cancelled = true;
    };
  }, [iconFile, iconColor, baseSvg, isInsert, type]);

  const onPickIcon = (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.svg') && file.type !== 'image/svg+xml') {
      toast.error('Envie um arquivo SVG.');
      return;
    }
    setIconFile(file);
  };

  const save = async () => {
    if (!label.trim() || !value.trim()) {
      toast.error('Preencha o rótulo e o conteúdo.');
      return;
    }
    setBusy(true);
    try {
      await saveFabButton({
        action: fab ? 'edit' : 'insert',
        id: fab?._id,
        idPassbook,
        label,
        type,
        previousType: fab?.type,
        // MGM: sempre a URL atual do passbook (nunca um valor digitado).
        value: type === 'mgm' ? mgmUrl : value,
        color,
        iconColor,
        previousIconColor: fab ? initialIconColor : undefined,
        existingSvgContent: !iconFile && baseSvg ? baseSvg : null,
        active,
        launchDirect: type === 'mgm' ? false : launchDirect,
        iconFile,
      });
      toast.success(fab ? 'Botão atualizado.' : 'Botão criado.');
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error('Falha ao salvar botão.');
    } finally {
      setBusy(false);
    }
  };

  const previewBlock = (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/20 p-3 sm:flex-col sm:items-center sm:gap-4 sm:p-6">
      <FabIcon
        iconUrl={!iconSvgPreview ? fab?.iconUrl : undefined}
        svgContent={iconSvgPreview}
        fallbackIcon={PreviewIcon}
        backgroundColor={color}
        iconColor={iconColor}
        className="size-14 shrink-0 rounded-full shadow-soft sm:size-20"
        iconClassName="size-6 sm:size-8"
      />
      <div className="min-w-0 flex-1 text-left sm:w-full sm:text-center">
        <p className="truncate text-sm font-medium sm:text-base">{label || 'Pré-visualização'}</p>
        <p className="truncate text-xs text-muted-foreground sm:text-sm">
          {isMgm ? 'Abre a página de indicação do cliente' : value || meta.placeholder}
        </p>
      </div>
    </div>
  );

  const steps: WizardStep[] = [
    {
      id: 'appearance',
      title: 'Aparência',
      icon: Palette,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {isInsert
              ? 'Envie um SVG e defina as cores. Se não enviar ícone, usamos um padrão.'
              : 'Ajuste as cores ou troque o SVG do botão.'}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".svg,image/svg+xml"
            className="hidden"
            onChange={(e) => onPickIcon(e.target.files?.[0])}
          />
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => fileInputRef.current?.click()}
          >
            <ImagePlus className="size-4 shrink-0" />
            <span className="min-w-0 truncate">
              {iconFile ? iconFile.name : isInsert ? 'Selecionar SVG' : 'Trocar SVG'}
            </span>
          </Button>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="fab-icon-color">Cor do ícone</Label>
              <ColorPicker id="fab-icon-color" value={iconColor} onChange={setIconColor} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fab-color">Cor de fundo</Label>
              <ColorPicker id="fab-color" value={color} onChange={setColor} />
            </div>
          </div>
          {previewBlock}
        </div>
      ),
    },
    {
      id: 'action',
      title: 'Ação',
      icon: MousePointerClick,
      validate: () => {
        if (!label.trim() || !value.trim()) {
          toast.error('Preencha o rótulo e o conteúdo.');
          return false;
        }
        return true;
      },
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fab-label">Rótulo</Label>
            <Input
              id="fab-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex.: Fale conosco"
            />
          </div>
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={type} onValueChange={(v) => changeType(v as FabButtonType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map((t) => (
                  <SelectItem key={t} value={t}>
                    {FAB_TYPE_META[t].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            {isMgm ? (
              <>
                <Label>{meta.valueLabel}</Label>
                <MgmFabLink url={mgmUrl} />
                <p className="text-xs text-muted-foreground">
                  O app completa o link com o CPF do cliente e abre a página de indicação dentro do
                  próprio app.
                </p>
              </>
            ) : (
              <>
                <Label htmlFor="fab-value">{meta.valueLabel}</Label>
                <Input
                  id="fab-value"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={meta.placeholder}
                />
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'settings',
      title: 'Configurações',
      icon: Settings2,
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] p-3">
            <div>
              <p className="text-sm font-medium">Abrir link externamente</p>
              <p className="text-xs text-muted-foreground">
                {isMgm
                  ? 'Indique e ganhe sempre abre dentro do app, em uma tela embutida.'
                  : launchDirect
                    ? 'O destino abre fora do app (navegador, WhatsApp, telefone, etc.).'
                    : 'O destino abre dentro do app, em uma tela embutida.'}
              </p>
            </div>
            <Switch checked={isMgm ? false : launchDirect} onCheckedChange={setLaunchDirect} disabled={isMgm} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] p-3">
            <div>
              <p className="text-sm font-medium">Ativo</p>
              <p className="text-xs text-muted-foreground">Exibir o botão no app do cliente.</p>
            </div>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>
          {isMgm ? (
            <MgmFabPreview
              url={mgmUrl}
              label={label}
              color={color}
              iconColor={iconColor}
              svgContent={iconSvgPreview}
              iconUrl={!iconSvgPreview ? fab?.iconUrl : undefined}
            />
          ) : (
            previewBlock
          )}
        </div>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92dvh,760px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 space-y-1 border-b border-border/60 px-4 py-3 sm:px-6 sm:py-4">
          <DialogTitle>{fab ? 'Editar botão' : 'Novo botão flutuante'}</DialogTitle>
          <DialogDescription className="hidden sm:block">Configure o botão em 3 passos.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-6 sm:py-4">
          <Wizard
            steps={steps}
            onFinish={save}
            onCancel={() => onOpenChange(false)}
            isSubmitting={busy}
            finishLabel={fab ? 'Salvar' : 'Criar botão'}
            mobileOptimized
            scrollableBody={false}
            className="gap-3 sm:gap-6"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
